// frontend/src/components/forecastgrid/ForecastGrid.jsx
// Single-row forecast grid — one row per Item+Location, SSRM pagination
// Optional sales history toggle: the 24 months before the forecast horizon,
// left of it

import { useEffect, useState, useCallback, useRef, useMemo, forwardRef, useImperativeHandle } from "react";
import { createPortal } from 'react-dom';
import { AgGridReact } from "ag-grid-react";
import { useTranslation } from 'react-i18next';
import useKeyboardSave from '../../hooks/useKeyboardSave';
import { useEditableGridState } from '../../hooks/useEditableGrid';

import ImportButton from '../common/ImportButton';
import FilterSwitch from '../common/FilterSwitch';
import useBulkImport from '../../hooks/useBulkImport';
import { DATA } from '../../queries/dataChanged';
import BulkImportDialog from '../common/BulkImportDialog';
import GridExportButton from '../../components/common/GridExportButton';
import { ItemFilterBar, drillDownFromCellClick, withDrillDownCursor } from '../../features/item-filter';
import { useAuth } from '../../contexts/AuthContext';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import { useForecastBulkUpsert } from '../../hooks/useForecastMutations';
import { translateRequestError } from '../../queries/api';
import { useAgGridTheme } from '../../utils/agGridTheme';
import { defaultColDef, defaultGridProps, editableGridProps, copyOnlyContextMenuItems, flexColumn } from '../../utils/gridDefaults';

import { useColumnTypes } from '../../hooks/useColumnTypes';
import { useFormatters } from '../../hooks/useFormatters';
import { useStoreToday } from '../../hooks/useStoreToday';
import { historyMonths, horizonMonths } from '../../utils/forecastHorizon';
import { parseClearableUnitsCell } from '../../utils/planningCellParsers';
import { buildForecastBatches, isForecastCellChange } from '../../utils/forecastBatches';
import { applyForecastMonthCell, buildForecastRows } from '../../utils/forecastRows';
import { buildPinnedTotalsRow, wrapColumnsForSummaryRows } from '../../utils/pinnedTotals';
import { useSsrmGrid, buildExportQuery } from '../../hooks/useSsrmGrid';
import OverrideCellRenderer from '../grid/OverrideCellRenderer';
import { useIsShopify, productColumn } from '../grid/productIdentity';
import { useProductGrouping } from '../grid/productGrouping';
import ForecastTooltip from '../grid/ForecastTooltip';
import { failedRowsMessage } from '../../utils/failedRows';
import './ForecastGrid.css';
import { GridEmptyState } from '../grid/NoRowsOverlay';
import { scrollToColumnOnceShown } from './scrollToColumn';
import { showToast } from '../../components/common/appToastBus';
import { GridPage, GridViewport } from '../grid/GridPage';


// ============================================================
// Helpers
// ============================================================
// Row fields this grid's drill-down reads (see drillDownFromCellClick).
// Rows are pivoted client-side in processForecasts, hence PascalCase keys
// rather than the wire's snake_case.
const DRILL_DOWN_FIELDS = {
  itemCode: 'ItemCode',
  itemName: 'ItemName',
  sku: 'ItemSKU',
  locationCode: 'LocationCode',
  locationName: 'LocationName',
};

// The forecast and sales-history months as grid columns. WHICH months is not
// decided here — that is utils/forecastHorizon.js. This only attaches labels.
const getMonthColumns = (fmtMonthYear, today) => (
  horizonMonths(today).map((key) => ({ key, label: fmtMonthYear(key) }))
);

const getHistoryMonthColumns = (fmtMonthYear, today) => (
  historyMonths(today).map((key) => ({ key, label: fmtMonthYear(key) }))
);


// ============================================================
// Main Component
// ============================================================
const ForecastGrid = forwardRef(function ForecastGrid({
  // Drill-down wiring (set by parent via useGridChartScope). Required: the
  // identity cells show a pointer, so a click on them must drill.
  onRowSelect,
  selectedRowId,
  // Lifecycle callbacks
  onEditingChange,
  // Page-owned filter scope (also from useGridChartScope) — the grid is a
  // controlled component for filters so the chart can mirror the same
  // population. Must be passed by the parent.
  filter,
  selectedLocation, setSelectedLocation,
  selectedProductCategory, setSelectedProductCategory,
  promotedOnly, setPromotedOnly,
  // DOM node inside the parent Tile's `headerActions` slot — the grid owns
  // the Bulk update and Export buttons (they need its state)
  // but renders them into the tile header via a portal.
  headerActionsEl,
}, ref) {
  const { t } = useTranslation();
  const { user, updatePreference } = useAuth();
  const isShopify = useIsShopify();
  const columnTypes = useColumnTypes();
  const { formatNumber, formatMonthYear, parseNumber } = useFormatters();
  // The store's day anchors which 12 months this grid offers — see
  // getMonthColumns above.
  const storeToday = useStoreToday();
  const gridApiRef = useRef(null);
  // Filled by useSsrmGrid below; the discard handler above it re-reads the
  // rows through this.
  const refreshGridRef = useRef(null);

  // Measure widest month header to set consistent width for all month columns
  const monthColWidth = useMemo(() => {
    const monthColumns = getMonthColumns(formatMonthYear, storeToday);
    // No months means no headers to measure, and `Math.max()` of nothing is
    // -Infinity — which would reach AG Grid as a column width. There are no
    // month columns to size in that case, so the value is unused; return a
    // number rather than propagating an infinity through the column defs.
    if (monthColumns.length === 0) return 0;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = '600 0.75rem -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    const maxHeaderWidth = Math.max(...monthColumns.map(m => ctx.measureText(m.label).width));
    return Math.ceil(maxHeaderWidth + 24); // cell padding
  }, [formatMonthYear, storeToday]);

  // Per-month totals across the filtered set — keyed by 'YYYY-MM' to match
  // the frontend's month column field names so they map directly into the
  // pinned-bottom row.
  const [totals, setTotals] = useState(null);

  // Sales history toggle — persisted in user preferences
  const [showHistory, setShowHistory] = useState(
    () => user?.ui_preferences?.forecast_show_history ?? false
  );
  // The history columns are always declared and only hidden while the switch
  // is off, so they keep their place in the grid's column order — the order
  // the merchant may have dragged inside them included.
  const historyMonthCols = useMemo(
    () => getHistoryMonthColumns(formatMonthYear, storeToday),
    [formatMonthYear, storeToday],
  );
  // Hidden by the switch, not by the merchant: kept out of their saved column
  // visibility, and hidden by every state restored while the switch is off.
  const historyHidden = useMemo(
    () => (showHistory ? [] : historyMonthCols.map((month) => `hist_${month.key}`)),
    [showHistory, historyMonthCols],
  );
  // Identifier columns — the product tree, the product cell and Location — are
  // pinned left when history is shown, so the months scroll under them.
  // Disabled on mobile, where pinning leaves no room for the months. Unpinned
  // is null: AG Grid skips a columnDefs `pinned` of undefined, so the columns
  // would stay pinned after the switch goes off.
  const identifierPinned = showHistory && window.innerWidth >= 768 ? 'left' : null;

  // Switching the mode refetches every row, so it runs through the same
  // unsaved-edits guard a filter change does (arrow indirection:
  // confirmDiscardEdits is declared below and only ever invoked on a click).
  const grouping = useProductGrouping({
    gridId: 'forecast_grid',
    preferenceKey: 'forecast_grouped',
    pageHidden: historyHidden,
    leafIdFields: ['rowId'],
    pinned: identifierPinned,
    // The months run in time order, sales history before the forecast.
    movableColumns: false,
    beforeToggle: () => confirmDiscardEdits(),
  });
  // Destructured so createColumnDefs can list the stable pieces as deps rather
  // than the whole object, which is new on every render.
  const { isGroupRow, isGrouped, columns: groupedColumns, variantProductColumn, totalsLabelField } = grouping;

  // Import — forecast import uses a dedicated endpoint on ForecastViewSet
  // (update-only, is_current filter, info bucket, consensus-only whitelist).
  // See ForecastViewSet.import_rows for the full scope.
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const importHook = useBulkImport({
    modelName: 'forecasts',
    endpoint: '/api/forecasts/import/',
    changes: [DATA.forecasts],
  });

  const gridTheme = useAgGridTheme();

  const FORECASTS_API = '/api/forecasts/';
  const pageSize = 100;
  const bulkUpsertMutation = useForecastBulkUpsert();


  // ============================================================
  // Data Fetching
  // ============================================================

  // ============================================================
  // Process Forecasts → Single Row per Item+Location
  // ============================================================
  // The pivot itself — and what each key on a row means — lives in
  // utils/forecastRows.js with its tests. Only the horizon is passed in here.
  const processForecasts = useCallback((forecasts, salesHistory) => (
    buildForecastRows(forecasts, {
      monthKeys: horizonMonths(storeToday),
      salesHistory,
      historyMonths: historyMonths(storeToday),
    })
  ), [storeToday]);


  // ============================================================
  // Server-Side Datasource
  // ============================================================
  const ssrmParams = useMemo(() => ({
    ...filter.params,
    location_code: selectedLocation === 'all' ? null : selectedLocation,
    product_category_id: selectedProductCategory?.id || null,
    include_history: showHistory ? 'true' : null,
    has_promotions: promotedOnly ? 'true' : null,
  }), [filter.params, selectedLocation, selectedProductCategory, showHistory, promotedOnly]);

  // The filtered export covers exactly the rows the grid lists
  // (`include_history` adds columns to the grid, not rows).
  const exportProps = {
    endpoint: '/api/forecasts/export/',
    filename: 'forecast',
    getParams: (scope) => (scope === 'all' ? '' : buildExportQuery(ssrmParams)),
  };

  // Product header rows arrive ready to render — their month and history
  // sums are keyed like the columns (ForecastViewSet._headers); leaf rows
  // need the months pivoted and the sales-history columns spliced in. The
  // hook calls this for every fetch to derive the array it hands to AG Grid.
  const transformForecastRows = useCallback((data, request) => {
    const rowGroupCols = request?.rowGroupCols || [];
    const groupKeys = request?.groupKeys || [];
    const isGroupRequest = rowGroupCols.length > 0 && groupKeys.length === 0;
    if (isGroupRequest) return data.results;
    return processForecasts(data.results, showHistory ? data.sales_history : null);
  }, [processForecasts, showHistory]);

  // The totals row spans the whole filtered set, so it is asked for once per
  // filter or sort change (the epoch's first block) and kept for the epoch:
  // only that block carries `totals` — later blocks and the leaves of an
  // opened product omit it, and overwriting on those would blank the row.
  const ssrmEpochParams = useMemo(() => ({ totals: 1 }), []);

  const handleSsrmData = useCallback((data) => {
    if (data.totals !== undefined) setTotals(data.totals);
  }, []);

  const handleSsrmError = useCallback((err) => {
    showToast(translateRequestError(err, t, 'forecast.messages.loadFailed'), 'error');
  }, [t]);

  const { onGridReady, onGridPreDestroyed, pagination } = useSsrmGrid({
    endpoint: FORECASTS_API,
    params: ssrmParams,
    epochParams: ssrmEpochParams,
    pageSize,
    transformRows: transformForecastRows,
    onData: handleSsrmData,
    onError: handleSsrmError,
    gridRef: gridApiRef,
    refreshRef: refreshGridRef,
  });


  // Drill-down: which clicks count and what they carry is the item-filter
  // feature's rule (drillDownFromCellClick); this grid only names its fields.
  const handleDrillDownClick = useCallback((event) => {
    const target = drillDownFromCellClick(event, DRILL_DOWN_FIELDS);
    if (target) onRowSelect(target);
  }, [onRowSelect]);
  const gridDefaultColDef = useMemo(
    () => withDrillDownCursor({ ...defaultColDef, ...grouping.defaultColDef }, DRILL_DOWN_FIELDS),
    [grouping.defaultColDef],
  );

  // ============================================================
  // Cell Editing — dirty tracking via useEditableGridState; the save
  // flow stays custom because we split cells into updates + creates
  // and patch row nodes from the response AFTER the await, while the
  // cell map still names them.
  // ============================================================
  const editing = useEditableGridState({ getRowId: (row) => row.rowId });
  // Stable callback — destructured so cellRendererParams / cellClassRules
  // can reference it inside createColumnDefs's deps without dragging the
  // whole `editing` object (which changes identity every render).
  const { unsavedCellClass, gridContext } = editing;

  const onCellValueChanged = useCallback((event) => {
    const { data, colDef, newValue, oldValue } = event;
    if (!/^\d{4}-\d{2}$/.test(colDef.field)) return;

    let parsedValue;
    if (newValue === null || newValue === undefined || newValue === '') {
      parsedValue = null;
    } else {
      parsedValue = parseNumber(newValue);
      if (isNaN(parsedValue)) {
        showToast(t('msg.invalidNumber'), "warning");
        event.node.setDataValue(colDef.field, oldValue);
        return;
      }
    }

    // Clearing a month the engine has not written leaves the cell exactly as
    // the server holds it, so any pending edit for it is dropped rather than
    // recorded — otherwise the save INSERTed a row with a null consensus. Rule
    // and reasoning in utils/forecastBatches.js.
    if (!isForecastCellChange(data, colDef.field, parsedValue)) {
      editing.clearCellsByPredicate(
        (info) => info.rowId === data.rowId && info.fieldName === colDef.field,
      );
    } else {
      editing.recordCellChange({
        row: data,
        fieldName: colDef.field,
        oldValue,
        newValue: parsedValue,
      });
    }

    event.api?.refreshCells({ force: true, rowNodes: [event.node], columns: [colDef.field] });
  }, [t, parseNumber, editing]);

  const handleSaveChanges = useCallback(async () => {
    const cellsRef = editing.getChangedCellsRef();
    if (cellsRef.size === 0) return;

    // The update/create split, and which engine-owned columns are deliberately
    // absent from a create, live in utils/forecastBatches.js. `pendingNodes` is
    // the snapshot the optimistic row-node patch below reads, taken here
    // because the cell map is cleared on success.
    const { updates, creates, pendingNodes, skipped } = buildForecastBatches(cellsRef.values());

    // `onCellValueChanged` drops these before they can become dirty, so a
    // non-zero count means that guard was bypassed and edits are about to be
    // cleared without being sent. Say so rather than let it pass as a clean save.
    if (skipped > 0) {
      console.error(
        `forecastBulkUpsert: ${skipped} dirty cell(s) had nothing to send — `
        + 'a cleared month with no forecast row should never have been recorded.',
      );
    }

    try {
      const data = await bulkUpsertMutation.mutateAsync({ updates, creates });

      editing.clearAll();

      // Patch local row data so the cells hold their saved values until the
      // grid's reload lands. The values come from the RESPONSE, not from what
      // was typed: the server stores `final - promotion` and answers with what
      // it actually holds, and a cleared cell comes back as the engine's
      // number. Patching the typed value instead would leave a promoted cell
      // showing a consensus in a column that renders finals.
      //
      // `pendingNodes` is parallel to `updates.concat(creates)` and the
      // endpoint answers in that same order (utils/forecastBatches.js), so
      // index is the correlation. A result missing at that index means the
      // counts diverged — leave the cell alone and let the reload be the
      // source of truth rather than crediting it to the wrong month.
      pendingNodes.forEach(({ rowId, monthKey }, index) => {
        const landed = data.results[index];
        if (!landed?.success) return;
        const node = gridApiRef.current?.getRowNode(rowId);
        if (!node) return;
        // A cleared month the engine never forecast has no row any more: the
        // cell goes back to "—" and the next edit here is a create, not an
        // update of an id that is gone. The consensus is NOT NULL on a row,
        // so a null one is the server saying there is no row.
        if (landed.ConsensusForecastUnits == null) {
          applyForecastMonthCell(node.data, monthKey, undefined);
          return;
        }
        // `_system` and `_statistical` keep their loaded values: a save writes
        // only the consensus and its flag (forecasting.consensus).
        node.data[monthKey] = landed.FinalForecastUnits;
        node.data[`${monthKey}_consensus`] = landed.ConsensusForecastUnits;
        node.data[`${monthKey}_promotion`] = landed.PromotionForecastUnits;
        // The flag lands with the rest of `landed_units` — a cleared cell
        // comes back False, so the cell drops the user ink without waiting for the
        // refresh below.
        node.data[`${monthKey}_user_modified`] = landed.ConsensusUserModified;
      });

      if (data.summary.failed > 0) {
        showToast(failedRowsMessage(t, data.results), 'warning');
      } else {
        showToast(t('msg.savedSuccessfully', { count: data.summary.succeeded }), 'success');
      }
    } catch (err) {
      console.error('forecastBulkUpsert:', err.message);
      showToast(translateRequestError(err, t, 'msg.saveFailed'), 'error');
    }
  }, [editing, bulkUpsertMutation, t]);

  const handleDiscardChanges = useCallback(() => {
    editing.clearAll();
    refreshGridRef.current({ purge: false });
    showToast(t('common.changesDiscarded'), 'info');
  }, [editing, t]);

  useKeyboardSave(handleSaveChanges, editing.isDirty && !bulkUpsertMutation.isPending);

  // Unsaved-edits guard for anything that reloads grid data underneath
  // pending cells: filter changes (wired into useGridChartScope's
  // beforeChange by the parent, via the imperative handle), the grouping and
  // sales-history switches, and the bulk Import button below.
  const { confirm, dialogProps } = useConfirmDialog();
  const confirmDiscardEdits = useCallback(async () => {
    if (!editing.isDirty) return true;
    const ok = await confirm({
      title: t('common.unsavedChangesTitle'),
      message: t('forecast.messages.unsavedChangesReload'),
      confirmLabel: t('common.discard'),
      variant: 'warning',
    });
    if (!ok) return false;
    editing.clearAll();
    return true;
  }, [editing, confirm, t]);

  // The history switch adds a request param, so it reloads every row and
  // asks first, as a filter change does. Turning the history on scrolls to
  // its first month, which may sit left of where the merchant had scrolled.
  // Without a grid yet there is nothing to scroll: it is built at its start.
  const historyScrollRef = useRef(null);
  const handleToggleHistory = useCallback(async (val) => {
    if (!(await confirmDiscardEdits())) return;
    setShowHistory(val);
    updatePreference('forecast_show_history', val);
    historyScrollRef.current?.();
    historyScrollRef.current = val && gridApiRef.current && historyMonthCols.length > 0
      ? scrollToColumnOnceShown(gridApiRef.current, `hist_${historyMonthCols[0].key}`)
      : null;
  }, [confirmDiscardEdits, updatePreference, historyMonthCols]);

  useImperativeHandle(ref, () => ({
    save: handleSaveChanges,
    discard: handleDiscardChanges,
    confirmDiscardEdits,
  }), [handleSaveChanges, handleDiscardChanges, confirmDiscardEdits]);

  // The page renders the Save / Discard pair, so it needs both halves of the
  // grid's editing state: how many cells wait and whether a save is running.
  useEffect(() => {
    onEditingChange?.({ count: editing.changedCells.size, saving: bulkUpsertMutation.isPending });
  }, [editing.changedCells.size, bulkUpsertMutation.isPending, onEditingChange]);


  // ============================================================
  // Column Definitions
  // ============================================================
  const createColumnDefs = useCallback((monthColumns, showingHistory, historyMonthColumns, pinned) => {
    const identifierCols = [
      // Shopify grouped view: the product tree (the auto group column, from
      // productGrouping), then one Product cell per variant row — variant
      // title and SKU; the product row carries the picture.
      ...groupedColumns([
        variantProductColumn({
          imageField: 'ImageUrl',
          nameField: 'ItemName',
          skuField: 'ItemSKU',
          codeField: 'ItemCode',
          variantField: 'shopify_variant_title',
          ...flexColumn(2),
          suppressHeaderMenuButton: true,
        }),
      ]),
      // Shopify flat view: one merchant-facing Product cell (image + name +
      // SKU) replaces the internal item_code / SKU / name trio — the ItemCode
      // is a Shopify variant-GID suffix that merchants never see in the admin.
      ...(isShopify && !isGrouped ? [
        productColumn({
          headerName: t('forecast.product'),
          imageField: 'ImageUrl',
          nameField: 'ItemName',
          skuField: 'ItemSKU',
          codeField: 'ItemCode',
          pinned,
          ...flexColumn(2),
          suppressHeaderMenuButton: true,
        }),
      ] : []),
      // Non-Shopify (ERP / manual) stores: ItemCode is a real, merchant-known
      // value — keep the explicit identifier columns.
      ...(!isShopify ? [
        {
          field: 'ItemCode',
          headerName: t('forecast.itemCode'),
          pinned,
          suppressHeaderMenuButton: true,
        },
        {
          field: 'ItemSKU',
          headerName: t('forecast.sku'),
          pinned,
          suppressHeaderMenuButton: true,
          type: 'optionalTextColumn',
        },
        {
          field: 'ItemName',
          headerName: t('forecast.itemName'),
          pinned,
          ...flexColumn(),
          suppressHeaderMenuButton: true,
        },
      ] : []),
      {
        field: 'LocationName',
        headerName: t('common.location'),
        pinned,
        suppressHeaderMenuButton: true,
      },
    ];

    // Forecast month columns — editable with custom renderer.
    // lockVisible + suppressColumnsToolPanel: month columns are the grid's
    // payload. Hiding them via the picker is almost always accidental and
    // persists across the rolling 12-month window, so calendar months
    // re-enter the window pre-hidden months later.
    const forecastChildren = monthColumns.map(month => ({
      field: month.key,
      headerName: month.label,
      width: monthColWidth,
      suppressAutoSize: true,
      lockVisible: true,
      suppressColumnsToolPanel: true,
      type: 'numberColumn',
      editable: (params) => !isGroupRow(params.data),
      // Month columns sort in both modes; everything else stops sorting when
      // the rows are grouped (productGrouping's defaultColDef).
      sortable: true,
      cellRenderer: OverrideCellRenderer,
      cellRendererParams: {
        // Everything the merchant did NOT type: the engine's forecast plus the
        // promotion, as the server computed it (utils/forecastRows.js).
        getSystemValue: (data) => data[`${month.key}_system`],
        // The user ink means "a person put this number here" — which is what
        // the server records in `ConsensusUserModified`, so the cell asks the
        // row rather than inferring it from consensus-vs-statistical. A
        // promotion the merchant scheduled is deliberately not part of the
        // question, and neither is a merchant who pinned the month to the
        // engine's own figure: that IS an override, and the old value
        // comparison called it untouched.
        isModified: (data, colDef) => (
          // An unsaved edit counts — the merchant is looking at their typing.
          unsavedCellClass({ data, colDef })
          || Boolean(data[`${month.key}_user_modified`])
        ),
      },

      // A blank clears the override and restores the statistical value; a
      // fraction rounds (this grid has no per-cell error surface to refuse it
      // with); the bound is the serializer's alone. Shared with the other
      // planning grids: utils/planningCellParsers.js.
      valueParser: (params) => parseClearableUnitsCell(params, parseNumber),

      tooltipComponent: ForecastTooltip,
      tooltipValueGetter: (params) => {
        // Return truthy value to trigger tooltip; actual rendering done by ForecastTooltip
        const stat = params.data[`${month.key}_statistical`];
        return stat !== null && stat !== undefined ? true : '';
      },

      cellClassRules: {
        'ag-cell-note-mark': (params) =>
          params.data[`${month.key}_promotion`] != null,
      },
    }));

    // Declared in both views and hidden while the switch is off (see
    // historyMonthCols). lockVisible + suppressColumnsToolPanel for the same
    // reason as forecast months — the showHistory page-level switch is the
    // single control for history visibility, not the column picker.
    const historyChildren = historyMonthColumns.map(month => ({
      field: `hist_${month.key}`,
      headerName: month.label,
      headerClass: 'history-col-header',
      hide: !showingHistory,
      width: monthColWidth,
      suppressAutoSize: true,
      lockVisible: true,
      suppressColumnsToolPanel: true,
      type: 'numberColumn',
      editable: false,
      sortable: false,
      cellClass: 'history-cell',
      valueFormatter: (params) =>
        params.value == null ? '—' : formatNumber(params.value, 0),
    }));

    // Without history: flat columns (no group headers)
    if (!showingHistory) {
      return [...identifierCols, ...historyChildren, ...forecastChildren];
    }

    // With history: column groups, and the line where the forecast starts
    // (styles/pages.css `.ag-column-group-start`) on the Forecast group header,
    // the first forecast month's header and its cells.
    if (forecastChildren.length > 0) {
      forecastChildren[0] = {
        ...forecastChildren[0],
        headerClass: 'ag-column-group-start',
        cellClass: 'ag-column-group-start',
      };
    }

    const historyGroup = {
      headerName: t('forecast.historyGroupHeader'),
      headerClass: 'history-header-group',
      marryChildren: true,
      children: historyChildren,
    };

    const forecastGroup = {
      headerName: t('forecast.forecastGroupHeader'),
      headerClass: 'ag-column-group-start',
      marryChildren: true,
      children: forecastChildren,
    };

    return [...identifierCols, historyGroup, forecastGroup];
  }, [t, monthColWidth, formatNumber, parseNumber, isShopify, unsavedCellClass, isGrouped, groupedColumns, variantProductColumn, isGroupRow]);

  // Column defs rebuild whenever createColumnDefs's deps change (incl. language via t).
  // Wrapped through wrapColumnsForSummaryRows so columns whose field isn't
  // part of the totals payload render empty on the totals and product rows instead of
  // their inline-formatter null fallback ('-' / '—').
  const columnDefs = useMemo(() => {
    const monthColumns = getMonthColumns(formatMonthYear, storeToday);
    return wrapColumnsForSummaryRows(
      createColumnDefs(monthColumns, showHistory, historyMonthCols, identifierPinned),
    );
  }, [createColumnDefs, formatMonthYear, storeToday, showHistory, historyMonthCols, identifierPinned]);

  // Pinned-bottom totals row. The backend returns totals keyed by 'YYYY-MM'
  // (forecast columns) and 'hist_YYYY-MM' (history columns when the merchant
  // has the sales-history toggle on) — both match the frontend column field
  // names directly. The "Total" label sits in the first column: when flat on
  // ItemCode (the ItemCode column on a non-Shopify store; on a Shopify store
  // the Product cell prints the row's code field as plain text on the pinned
  // row, ProductCell.jsx), when grouped in the product tree column
  // (productGrouping's totalsLabelField).
  const pinnedBottomRowData = useMemo(
    () => buildPinnedTotalsRow(totals, totalsLabelField('ItemCode'), t('grid.totals.label')),
    [totals, t, totalsLabelField],
  );

  // ============================================================
  // Render
  // ============================================================
  return (
    <GridPage>
      {/* Filters */}
      {headerActionsEl && createPortal(
        <>
          <ImportButton
            entityKey="forecast"
            onClick={async () => {
              // A bulk update refreshes the grid on success — pending cells
              // would be silently clobbered without the guard filters use.
              if (await confirmDiscardEdits()) setImportDialogOpen(true);
            }}
          />
          <GridExportButton {...exportProps} />
        </>,
        headerActionsEl,
      )}

      <ItemFilterBar
        filter={filter}
        productCategoryFilter={{ selected: selectedProductCategory, set: setSelectedProductCategory, show: true }}
        locationFilter={{ selected: selectedLocation, set: setSelectedLocation }}
        actions={
          <>
            <FilterSwitch
              label={t('promotions.hasPromotions')}
              checked={promotedOnly}
              onChange={(on) => filter.guarded(() => setPromotedOnly(on))}
            />
            <FilterSwitch
              label={t('forecast.showHistory')}
              checked={showHistory}
              onChange={handleToggleHistory}
            />
            {grouping.toggle}
          </>
        }
      />

      {/* Grid */}
      <GridViewport>
        {gridTheme && (
          <>
            <GridEmptyState
              title={t('forecast.empty.title')}
              // External filters (ItemFilterBar) bypass AG Grid, so
              // the overlay can't infer this on its own.
              filtersActive={selectedLocation !== 'all' || !!selectedProductCategory || !!filter.filter || promotedOnly}
            >
              <AgGridReact
                {...defaultGridProps}
                {...editableGridProps}
                context={gridContext}
                theme={gridTheme}
                columnDefs={columnDefs}
                defaultColDef={gridDefaultColDef}
                columnTypes={columnTypes}
                {...grouping.gridProps}
                onCellValueChanged={onCellValueChanged}
                onCellClicked={handleDrillDownClick}
                onGridReady={onGridReady}
                onGridPreDestroyed={onGridPreDestroyed}
                rowClassRules={{
                  'forecast-row-active': (params) => params.data?.rowId === selectedRowId,
                }}
                getContextMenuItems={copyOnlyContextMenuItems}
                rowModelType="serverSide"
                {...pagination}
                pinnedBottomRowData={pinnedBottomRowData}
              />
            </GridEmptyState>
          </>
        )}
      </GridViewport>

      {/* Import Dialog */}
      <BulkImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        importHook={importHook}
        entityKey="forecast"
      />

      {/* Unsaved-edits guard prompt */}
      <ConfirmDialog {...dialogProps} />
    </GridPage>
  );
});

export default ForecastGrid;
